import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Delete } from "lucide-react";
import { useApp } from "../context/AppContext";
import Avatar from "../components/Avatar";
import ScreenHeader from "../components/ScreenHeader";
import PartnerBadge from "../components/PartnerBadge";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", ".", "0", "back"];

export const AmountScreen = () => {
  const nav = useNavigate();
  const { draft, updateDraft, computeFee, partner, balance } = useApp();
  const recipient = draft.recipient;

  if (!recipient) {
    nav("/send/recipient", { replace: true });
    return null;
  }

  const amountStr = draft.amount || "";
  const amountNum = Number(amountStr) || 0;
  const fee = computeFee(amountNum);
  const recipientGets = amountNum > 0 ? Math.round((amountNum - fee) * 100) / 100 : 0;

  const handleKey = (k) => {
    let next = amountStr;
    if (k === "back") {
      next = next.slice(0, -1);
    } else if (k === ".") {
      if (!next.includes(".")) next = (next || "0") + ".";
    } else {
      // limit decimals to 2
      if (next.includes(".") && next.split(".")[1]?.length >= 2) return;
      if (next === "0") next = k;
      else next = next + k;
    }
    if (Number(next) > 999999) return;
    updateDraft({ amount: next });
  };

  const canContinue = amountNum >= 1 && amountNum <= balance;
  const isOverBalance = amountNum > balance;

  return (
    <motion.div
      initial={{ x: 20, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: -20, opacity: 0 }}
      transition={{ type: "spring", bounce: 0, duration: 0.35 }}
      className="h-full flex flex-col"
      data-testid="amount-screen"
    >
      <ScreenHeader title="Amount" testId="amount-back-btn" />

      {/* Recipient chip */}
      <div className="px-6 flex items-center gap-3 pb-2">
        <Avatar initials={recipient.initials} color={recipient.color} size={36} />
        <div>
          <div className="text-[11px] text-gray-500 font-medium">To</div>
          <div className="text-[14px] font-semibold text-gray-900 dark:text-white leading-tight">
            {recipient.name}
          </div>
        </div>
      </div>

      {/* Amount display */}
      <div className="flex-1 flex flex-col items-center justify-center px-6">
        <div className="text-[11px] uppercase tracking-widest text-gray-400 font-bold mb-3">
          You send
        </div>
        <div className="flex items-start gap-1">
          <span className="font-display text-3xl font-bold text-gray-400 mt-3">kr</span>
          <span
            data-testid="amount-display"
            className={`font-display text-[64px] leading-none font-bold tracking-tighter ${isOverBalance ? "text-red-500" : "text-gray-900 dark:text-white"}`}
          >
            {amountStr || "0"}
          </span>
        </div>

        {isOverBalance && (
          <div className="mt-2 text-sm text-red-500 font-medium">
            Insufficient balance (Available: {balance} kr)
          </div>
        )}

        <div className="mt-6 w-full max-w-[280px] rounded-2xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 px-4 py-3 space-y-2">
          <div className="flex items-center justify-between text-[12px]">
            <span className="text-gray-500">Fees</span>
            <span data-testid="fee-value" className="font-semibold text-gray-900 dark:text-white">
              {fee.toFixed(2)} kr
            </span>
          </div>
          <div className="h-px bg-gray-200 dark:bg-white/10" />
          <div className="flex items-center justify-between text-[13px]">
            <span className="text-gray-700 dark:text-gray-300 font-medium">Recipient gets</span>
            <span data-testid="recipient-gets-value" className="font-bold text-emerald-600 dark:text-emerald-400">
              {recipientGets.toFixed(2)} kr
            </span>
          </div>
        </div>

        <PartnerBadge partner={partner.name} variant="pill" className="mt-4" />
      </div>

      {/* Dialpad */}
      <div className="px-6 pt-2 pb-4">
        <div className="grid grid-cols-3 gap-1">
          {KEYS.map((k) => (
            <button
              key={k}
              data-testid={`dialpad-${k}`}
              onClick={() => handleKey(k)}
              className="dialpad-key h-14 rounded-xl flex items-center justify-center text-[22px] font-semibold text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-white/10 transition-colors"
            >
              {k === "back" ? <Delete className="w-5 h-5 text-gray-900 dark:text-white" strokeWidth={2} /> : k}
            </button>
          ))}
        </div>
        <div className="text-center text-[10px] text-gray-400 mb-2 font-medium">
          Demo Tip: Enter an amount ending in .99 to simulate failure
        </div>
        <button
          data-testid="amount-continue-btn"
          disabled={!canContinue}
          onClick={() => nav("/send/confirm")}
          className="mt-3 w-full bg-gray-900 dark:bg-emerald-600 text-white h-14 rounded-full font-semibold text-[15px] hover:bg-black dark:hover:bg-emerald-700 active:scale-[0.98] transition-all disabled:bg-gray-200 dark:disabled:bg-white/10 disabled:text-gray-400 disabled:cursor-not-allowed"
        >
          Continue
        </button>
      </div>
    </motion.div>
  );
};

export default AmountScreen;
